import React from 'react';
import PropTypes from 'prop-types';
import {Formik} from 'formik';
import Step13View from './Step13-view';
import {checkRequiredValues} from '../../utils/forms/checkRequiredValues';
import {loadOptionsByDDType} from '../../actions/loadOptionsByDDType';
import {ddTypes} from '../../constants/dropdownTypes';
import {fileTypesIds} from '../../constants/fileTypeIds';
import types from '../../types';
import {checkNextStepAndGo} from '../../utils/checkNextStepAndGo';
import {
  convertISOStringToDateWithTimezoneOffset,
  convertDateWithTimezoneOffsetToEpoch
} from '../../utils/forms/normalizeTime';
import {useIntl} from "react-intl";
import {trackEvent} from "../../utils/trackEvent";

const getCountOption = value => {
  return !isNaN(parseInt(value, 10))
    ? {
      label: `${value}`,
      value: `${value}`,
    }
    : ''
};

const Step13 = ({
                  submitStep,
                  uploadFile,
                  language,
                  regType,
                  payload,
                  prevCompletedStepSign,
                  prevCompletedStepSignFalse,
                }) => {
  const {
    newResidentType,
    forChildrenBornCount,
    forChildrenUpTo5Count,
    forChildrenUpTo17Count,
    forChildrenUpTo18Count,
    forToddlersBornCount,
    forToddlersUpTo5Count,
    specialResidentLocationFromDate,
    newResidentFromDate,
    newResidentNoIncomeToDate,
    retiredSoldierServiceStartDate,
    retiredSoldierServiceEndDate,
    ...restPayload
  } = payload;

  const initialValues = {
    israelResident: false,
    disabled: false,
    uploadBlindCertificate: null,
    specialResidentLocation: false,
    specialResidentLocationFromDate: (specialResidentLocationFromDate) ? convertISOStringToDateWithTimezoneOffset(specialResidentLocationFromDate) : null,
    specialResidentLocationName: null,
    uploadApprovalOfAuthority: null,
    newResident: false,
    newResidentType: newResidentType ? {value: newResidentType} : '',
    newResidentFromDate: (newResidentFromDate) ? convertISOStringToDateWithTimezoneOffset(newResidentFromDate) : null,
    newResidentNoIncomeToDate: (newResidentNoIncomeToDate) ? convertISOStringToDateWithTimezoneOffset(newResidentNoIncomeToDate) : null,
    uploadImmigrationCertificate: null,
    uploadReturningCertificate: null,
    spouseHasNoIncome: false,
    singleParentNoCustody: false,
    forChildren: false,
    forChildrenBornCount: getCountOption(forChildrenBornCount),
    forChildrenUpTo5Count: getCountOption(forChildrenUpTo5Count),
    forChildrenUpTo17Count: getCountOption(forChildrenUpTo17Count),
    forChildrenUpTo18Count: getCountOption(forChildrenUpTo18Count),
    forToddlers: false,
    forToddlersBornCount: getCountOption(forToddlersBornCount),
    forToddlersUpTo5Count: getCountOption(forToddlersUpTo5Count),
    singleParentCustody: false,
    childAlimony: false,
    disabledChild: false,
    uploadApprovalOfDisabledChild: null,
    spouseAlimony: false,
    uploadVerdict: null,
    retiredSoldier: false,
    retiredSoldierServiceStartDate: (retiredSoldierServiceStartDate) ? convertISOStringToDateWithTimezoneOffset(retiredSoldierServiceStartDate) : null,
    retiredSoldierServiceEndDate: (retiredSoldierServiceEndDate) ? convertISOStringToDateWithTimezoneOffset(retiredSoldierServiceEndDate) : null,
    uploadCertificateOfRelease: null,
    graduation: false,
    uploadStatementF119: null,
    under18: false,
    ...restPayload,
  };

  const validate = values => {
    const {
      specialResidentLocation,
      specialResidentLocationName,
      specialResidentLocationFromDate,
      newResident,
      newResidentType,
      newResidentFromDate,
      newResidentNoIncomeToDate,
      forChildren,
      forChildrenBornCount,
      forChildrenUpTo5Count,
      forChildrenUpTo17Count,
      forChildrenUpTo18Count,
      forToddlers,
      forToddlersBornCount,
      forToddlersUpTo5Count,
      retiredSoldier,
      retiredSoldierServiceStartDate,
      retiredSoldierServiceEndDate,
    } = values;
    let errors = {};

    if (specialResidentLocation) {
      errors = checkRequiredValues({specialResidentLocationName, specialResidentLocationFromDate}, errors)
    }

    if (newResident) {
      errors = checkRequiredValues({
        newResidentType,
        newResidentFromDate,
        newResidentNoIncomeToDate,
      }, errors)
    }

    if (forChildren) {
      errors = checkRequiredValues({
        forChildrenBornCount,
        forChildrenUpTo5Count,
        forChildrenUpTo17Count,
        forChildrenUpTo18Count,
      }, errors)
    }

    if (forToddlers) {
      errors = checkRequiredValues({
        forToddlersBornCount,
        forToddlersUpTo5Count,
      }, errors)
    }

    if (retiredSoldier) {
      errors = checkRequiredValues({
        retiredSoldierServiceStartDate,
        retiredSoldierServiceEndDate,
      }, errors)
    }

    return errors;
  };

  const {formatMessage} = useIntl();

  const handleSubmit = values => {
    Promise.resolve()
      .then(() => submitFiles(values))
      .then(() => submitFields(values))
      .then(checkNextStepAndGo)
      .then(() => prevCompletedStepSignFalse())
      .then(() => trackEvent('signup_step',
          {
            step_number: 13,
            step_name_en: 'Income tax',
            step_name_he: formatMessage({id: 'step13.page.title', defaultMessage: ''}),
            regType
          }
        )
      )
      .catch(err => console.log({err}))
  };

  const submitFiles = values => {
    const {
      disabled,
      uploadBlindCertificate,
      specialResidentLocation,
      uploadApprovalOfAuthority,
      newResident,
      newResidentType,
      uploadImmigrationCertificate,
      uploadReturningCertificate,
      disabledChild,
      uploadApprovalOfDisabledChild,
      spouseAlimony,
      uploadVerdict,
      retiredSoldier,
      uploadCertificateOfRelease,
      graduation,
      uploadStatementF119,
    } = values;
    const files = [];
    if (disabled && uploadBlindCertificate) files.push({
      file: uploadBlindCertificate[0],
      fileTypeId: fileTypesIds.step13.disabled
    });
    if (specialResidentLocation && uploadApprovalOfAuthority) files.push({
      file: uploadApprovalOfAuthority[0],
      fileTypeId: fileTypesIds.step13.permanentResidence
    });
    if (newResident && uploadImmigrationCertificate && newResidentType.value === 1) files.push({
      file: uploadImmigrationCertificate[0],
      fileTypeId: fileTypesIds.step13.newImmigrant
    });
    if (newResident && uploadReturningCertificate && newResidentType.value === 2) files.push({
      file: uploadReturningCertificate[0],
      fileTypeId: fileTypesIds.step13.returningCitizen
    });
    if (disabledChild && uploadApprovalOfDisabledChild) files.push({
      file: uploadApprovalOfDisabledChild[0],
      fileTypeId: fileTypesIds.step13.disabledKid
    });
    if (spouseAlimony && uploadVerdict) files.push({
      file: uploadVerdict[0],
      fileTypeId: fileTypesIds.step13.spouseAlimony
    });
    if (retiredSoldier && uploadCertificateOfRelease) files.push({
      file: uploadCertificateOfRelease[0],
      fileTypeId: fileTypesIds.step13.militaryDischarge
    });
    if (graduation && uploadStatementF119) files.push({
      file: uploadStatementF119[0],
      fileTypeId: fileTypesIds.step13.graduation
    });

    return files.map(file => uploadFile(file));
  };

  const submitFields = values => {
    const {
      israelResident,
      disabled,
      specialResidentLocation,
      specialResidentLocationName,
      specialResidentLocationFromDate,
      newResident,
      newResidentType,
      newResidentFromDate,
      newResidentNoIncomeToDate,
      spouseHasNoIncome,
      singleParentNoCustody,
      forChildren,
      forChildrenBornCount,
      forChildrenUpTo5Count,
      forChildrenUpTo17Count,
      forChildrenUpTo18Count,
      forToddlers,
      forToddlersBornCount,
      forToddlersUpTo5Count,
      singleParentCustody,
      childAlimony,
      disabledChild,
      spouseAlimony,
      retiredSoldier,
      retiredSoldierServiceStartDate,
      retiredSoldierServiceEndDate,
      graduation,
      under18,
    } = values;

    return submitStep({
      step: 13,
      data: {
        regType,
        israelResident,
        disabled,
        specialResidentLocation,
        specialResidentLocationName,
        specialResidentLocationFromDate: convertDateWithTimezoneOffsetToEpoch(specialResidentLocationFromDate),
        newResident,
        newResidentType: newResidentType.value,
        newResidentFromDate: convertDateWithTimezoneOffsetToEpoch(newResidentFromDate),
        newResidentNoIncomeToDate: convertDateWithTimezoneOffsetToEpoch(newResidentNoIncomeToDate),
        spouseHasNoIncome,
        singleParentNoCustody,
        forChildren,
        forChildrenBornCount: parseInt(forChildrenBornCount.value),
        forChildrenUpTo5Count: parseInt(forChildrenUpTo5Count.value),
        forChildrenUpTo17Count: parseInt(forChildrenUpTo17Count.value),
        forChildrenUpTo18Count: parseInt(forChildrenUpTo18Count.value),
        forToddlers,
        forToddlersBornCount: parseInt(forToddlersBornCount.value),
        forToddlersUpTo5Count: parseInt(forToddlersUpTo5Count.value),
        singleParentCustody,
        childAlimony,
        disabledChild,
        spouseAlimony,
        retiredSoldier,
        retiredSoldierServiceStartDate: convertDateWithTimezoneOffsetToEpoch(retiredSoldierServiceStartDate),
        retiredSoldierServiceEndDate: convertDateWithTimezoneOffsetToEpoch(retiredSoldierServiceEndDate),
        graduation,
        under18,
        prevCompletedStepSign
      },
    })
  };

  const loadOptions = (inputValue, callback) => {
    loadOptionsByDDType({callback, language, ddType: ddTypes.immigrantTypesItems})
  };

  const loadChildrenOptions = (inputValue, callback) => {
    const opts = new Array(11)
      .fill('child')
      .map((_, index) => {
        return {
          label: `${index}`,
          value: `${index}`,
        }
      });
    callback(opts);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      enableReinitialize={true}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {({values, isValid, submitCount, ...rest}) => (
        <Step13View
          values={values}
          loadOptions={{
            immigrant: loadOptions,
            childrenOpts: loadChildrenOptions
          }}
          isValid={isValid || submitCount === 0}
          {...rest}
        />
      )}
    </Formik>
  )
};

Step13.propTypes = {
  ...types.router,
  regType: PropTypes.string,
  language: PropTypes.string,
  payload: PropTypes.object,
  submitStep: PropTypes.func,
  uploadFile: PropTypes.func
};

export default Step13;
